import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import i18n, { _t } from '../utils/i18n.js';

// "Julien GRELET" -> "JG". Falls back to a single initial for a one-word name, and
// to nothing at all rather than rendering an empty circle.
const initialsOf = (name) => (name || '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((word) => word[0].toUpperCase())
  .join('');

const displayNameOf = (currentUser) => {
  const raw = currentUser?.username;
  if (!raw) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const UserMenu = ({ currentUser, logout, darkMode, toggleDarkMode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    if (logout) {
      logout();
    }
    setIsMenuOpen(false);
  };

  const displayName = displayNameOf(currentUser);
  const initials = initialsOf(displayName);

  return (
    <div className="relative">
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-expanded={isMenuOpen}
        className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 outline-none hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        {initials && (
          <span
            className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#e6e1fa] text-[13px] font-semibold text-[#4930c4] dark:bg-[#2a2440] dark:text-[#a98ff5]"
            aria-hidden
          >
            {initials}
          </span>
        )}
        <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
          {displayName || 'Paramètres'}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-3.5 w-3.5 text-gray-400 transition-transform dark:text-gray-500 ${isMenuOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50">
            <div className="py-1">
              {currentUser?.isadmin && (
                <button
                  onClick={() => {
                    navigate('/admin');
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <div className="flex items-center space-x-2">
                    Administration
                  </div>
                </button>
              )}

              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-200">Theme</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDarkMode();
                  }}
                  className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Toggle dark mode"
                >
                  {darkMode ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </button>
              </div>

              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-200">{_t('Language')}</span>
                <div className="flex space-x-2">
                  <button
                    className="hover:opacity-75 transition-opacity"
                    onClick={() => i18n.changeLanguage('fr')}
                  >
                    <span className="text-xl" role="img" aria-label="Français">🇫🇷</span>
                  </button>
                  <button
                    className="hover:opacity-75 transition-opacity"
                    onClick={() => i18n.changeLanguage('en')}
                  >
                    <span className="text-xl" role="img" aria-label="English">🇬🇧</span>
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700"></div>

              <button
                onClick={() => {
                  navigate('/');
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Accueil
              </button>

              {currentUser && logout && (
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Déconnexion
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserMenu;